import React from 'react';
import Select from 'react-select';

const SearchableCountrySelect = ({
  countryData,
  value,
  onChange,
  className = '',
  placeholder = 'Select Country',
  isRequired = false,
  height = '44px',
}) => {
  // Transform country data to react-select format
  const options = countryData?.data?.map((country) => ({
    value: country.alpha2Code || country.iso2 || '',
    label: country.name,
  })) || [];

  // Find the selected option
  const selectedOption = options.find((opt) => opt.value === value) || null;

  // Handle change
  const handleChange = (selectedOption) => {
    if (onChange) {
      onChange(selectedOption);
    }
  };

  // Custom styles - Minimalist professional design
  const customStyles = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: state.isFocused ? '#0226BE' : '#d1d5db',
      borderWidth: '1px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(2, 38, 190, 0.1)' : 'none',
      borderRadius: '6px',
      minHeight: height,
      fontSize: '14px',
      backgroundColor: '#fff',
      paddingLeft: '16px',
      paddingRight: '12px',
      '&:hover': {
        borderColor: state.isFocused ? '#0226BE' : '#9ca3af',
      },
      transition: 'all 0.2s ease-in-out',
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      backgroundColor: state.isSelected
        ? '#0226BE'
        : state.isFocused
        ? '#f6f9ff'
        : '#fff',
      color: state.isSelected ? '#fff' : '#333',
      cursor: 'pointer',
      fontSize: '14px',
      padding: '10px 12px',
      '&:active': {
        backgroundColor: '#0226BE',
        color: '#fff',
      },
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      borderRadius: '6px',
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
      zIndex: 9999,
      border: '1px solid #e5e7eb',
      marginTop: '4px',
    }),
    menuList: (baseStyles) => ({
      ...baseStyles,
      padding: '4px',
      maxHeight: '250px',
    }),
    placeholder: (baseStyles) => ({
      ...baseStyles,
      color: '#999',
      fontSize: '14px',
    }),
    input: (baseStyles) => ({
      ...baseStyles,
      fontSize: '14px',
      color: '#333',
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      fontSize: '14px',
      color: '#333',
    }),
    indicatorSeparator: (baseStyles) => ({
      ...baseStyles,
      display: 'none',
    }),
    dropdownIndicator: (baseStyles, state) => ({
      ...baseStyles,
      color: state.isFocused ? '#0226BE' : '#6b7280',
      padding: '8px',
      '&:hover': {
        color: '#0226BE',
      },
      transition: 'color 0.2s ease-in-out',
    }),
  };

  // Show loading state
  if (countryData?.isLoading) {
    return (
      <div
        className={`w-full border border-gray-300 bg-white rounded-md pl-4 pr-4 text-gray-500 font-[400] text-[14px] flex items-center ${className}`}
        style={{ height }}
      >
        Loading Countries...
      </div>
    );
  }

  // Show error state
  if (countryData?.error) {
    return (
      <div
        className={`w-full border border-red-300 bg-red-50 rounded-md pl-4 pr-4 text-red-600 font-[400] text-[14px] flex items-center ${className}`}
        style={{ height }}
      >
        Error loading countries. Please refresh the page.
      </div>
    );
  }

  return (
    <div className={className}>
      <Select
        value={selectedOption}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        isSearchable={true}
        isClearable={!isRequired}
        styles={customStyles}
        classNamePrefix="country-select"
        noOptionsMessage={({ inputValue }) =>
          inputValue ? `No country found for "${inputValue}"` : 'No countries available'
        }
      />
    </div>
  );
};

export default SearchableCountrySelect;
